import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../lib/axiosInstance';

export const APPROVAL_API = '/approvals';

/** One document waiting on this user's decision. */
export interface PendingApproval {
  id: string;
  module: string;
  code: string;
  submittedBy?: string | null;
  /** Epoch milliseconds, as the API serialises timestamps. */
  submittedAt?: number | string | null;
}

/** One step in a document's life, as the detail screens show it. */
export interface StatusLogEntry {
  id: string;
  module: string;
  dataId: string;
  status: string;
  statusDate?: string;
  remarks?: string;
  createdBy?: string;
  timeCreated?: string;
  actedFor?: string;
  entryType?: 'SUBMIT' | 'DECISION' | null;
}

/**
 * Everything waiting on this user, across every module they can approve.
 *
 * The permission flags have always said who may approve; nothing ever said
 * what was waiting. Admins see every submitted document.
 *
 * "Waiting" means somebody sent it, not merely that it rests at CONFIRM: 7,811
 * bills of material sit at CONFIRM going back to 2013, and none of them is
 * waiting on anyone.
 *
 * This year by default. The older submissions are still real work to clear,
 * so they stay one click away rather than being filtered out of existence.
 */
export const usePendingApprovals = (includeEarlier = false) =>
  useQuery({
    queryKey: [APPROVAL_API, 'pending', includeEarlier],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`${APPROVAL_API}/pending`, {
        params: { all: includeEarlier },
      });
      return data as PendingApproval[];
    },
  });

/** The badge on the menu: how many documents are waiting. */
export const usePendingApprovalCount = () =>
  useQuery({
    queryKey: [APPROVAL_API, 'pending', 'count'],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`${APPROVAL_API}/pending/count`);
      return {
        count: (data?.count ?? 0) as number,
        /** How many more are waiting from before this year. */
        earlier: (data?.earlier ?? 0) as number,
      };
    },
    // The queue changes when other people submit, not when this tab acts.
    refetchInterval: 60_000,
  });
